import React, { useEffect, useState } from 'react';
import {
  Heading,
  VStack,
  Text,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Box,
  HStack,
  Container,
  Icon,
  SimpleGrid,
  GridItem,
  useColorModeValue
} from "@chakra-ui/react";
import { FaMoneyBillWave, FaFutbol, FaRunning } from 'react-icons/fa';
import MemberActivityDetails from '../components/member-activity-details';
import ActivityPaymentHistory from '../components/activity-payment-history';
import ProductsToPayList from '../components/products-to-pay-list';
import PaymentMethods from '../components/payment-methods';
import LoadingSpinner from '../components/loading-spinner';
import { useAuth } from '../components/auth';
import { getMember } from '../services/member-service';
import { Activity } from '../models/activity';
import { getActivitiesFeesByMember } from "../services/product-service";
import { ActivityProductPayment } from "../models/activities-fees-by-member-response";
import { HTTP_STATUS_CODE_OK } from "../constants";
import { PeriodicProduct } from "../models/periodic-product";
import { MemberProductStatus } from "../models/periodic-product-payment";

function MyActivities(): JSX.Element {
  const auth = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [memberActivities, setMemberActivities] = useState<Activity[]>([]);
  const [memberActivitiesNoInsurance, setMemberActivitiesNoInsurance] = useState<Activity[]>([]);
  const [activitiesFeesYears, setActivitiesFeesYears] = useState<string[]>([]);
  const [activitiesFeesPayments, setActivitiesFeesPayments] = useState(new Map<string, ActivityProductPayment[]>());
  const [insuranceProductsPaid, setInsuranceProductsPaid] = useState<PeriodicProduct[]>([]);
  const [activitiesFeesToPayPayments, setActivitiesFeesToPayPayments] = useState<MemberProductStatus[]>([]);
  const [subtitleText, setSubtitleText] = useState("");

  const cardBg = useColorModeValue('white', 'gray.800');
  const headerBg = useColorModeValue('green.100', 'green.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const secondaryTextColor = useColorModeValue('gray.600', 'gray.300');

  useEffect(() => {
    const getMemberActivities = async (): Promise<void> => {
      try {
        const member = (await getMember(auth?.user?.selectedMember?.id)).response;
        if (member != null) {
          member.memberActivities.length > 0
            ? setSubtitleText("Estas son las actividades en las que estás dado/a de alta. Podés abonar los aranceles de actividad pendientes de pago marcando la casilla \"Seleccionar para abonar\" correspondiente a cada mes.")
            : setSubtitleText("Actualmente no estás dado de alta en ninguna actividad.");

          setMemberActivities(member?.memberActivities.map((x) => x.activity));
          setMemberActivitiesNoInsurance(member?.memberActivities.filter((activity) => !activity.activity.isInsurance && !activity.endDate).map(x => x.activity));
          const activitiesFeesResponse = await getActivitiesFeesByMember(member.id);
          if (activitiesFeesResponse.statusCode === HTTP_STATUS_CODE_OK && activitiesFeesResponse.response) {
            const keys = Object.keys(activitiesFeesResponse.response.activityFeesPayments);
            const entries = Object.entries(activitiesFeesResponse.response.activityFeesPayments);
            const map = new Map(entries);
            setActivitiesFeesYears(keys.reverse());
            setActivitiesFeesPayments(map);
            setInsuranceProductsPaid(
              getListOfPaidInsuranceProducts(
                map,
                member?.memberActivities.map((x) => x.activity)
              ) ?? []
            );
          }
        }
      } finally {
        setIsLoading(false);
      }
    }

    getMemberActivities().catch(() => setIsLoading(false));
  }, [auth?.user?.selectedMember?.id]);

  const getListOfPaidInsuranceProducts = (map: Map<string, ActivityProductPayment[]>, memberActivities: Activity[]) => {
    const insuranceActivity = memberActivities.find((insurance) => insurance.isInsurance);
    if (!insuranceActivity) return;

    const list: PeriodicProduct[] = [];
    map.forEach((activityProductPaymentArray) => {
      activityProductPaymentArray.map((activityProductPayment) => {
        if (activityProductPayment.activityId === insuranceActivity.id) {
          activityProductPayment.productPayments.map((productPayment): void => {
            if (productPayment.payments && productPayment.payments.length > 0) {
              list.push(productPayment.product);
            }
          })
        }
      })
    });
    return list;
  }

  const getInsuranceProductToPay = (insuranceActivityId: string, startDate: Date, endDate: Date) => {
    const year = new Date(startDate).getUTCFullYear().toString();
    const insuranceActivityPayments = activitiesFeesPayments.get(year)?.find((activityProductPayment) => activityProductPayment.activityId === insuranceActivityId);
    return insuranceActivityPayments?.productPayments.find((productPayment) => productPayment.product.startDate >= startDate && productPayment.product.endDate <= endDate)?.product;
  }

  const getInsuranceProductToPayPayment = (insuranceActivityId: string, startDate: Date, endDate: Date) => {
    const year = new Date(startDate).getUTCFullYear().toString();
    const insuranceActivityPayments = activitiesFeesPayments.get(year)?.find((activityProductPayment) => activityProductPayment.activityId === insuranceActivityId);
    return insuranceActivityPayments?.productPayments.find((productPayment) => productPayment.product.startDate >= startDate && productPayment.product.endDate <= endDate);
  }

  const handleCheckboxChange = (activityId: string, periodicProductCheckbox: MemberProductStatus) => {
    const index = activitiesFeesToPayPayments.findIndex((periodicProductPayment) => periodicProductPayment.product.id === periodicProductCheckbox.product.id);
    const activity = memberActivities.find((activity) => activity.id === activityId);
    if (!activity) return;

    let paidInsuranceIndex = -1;
    let toPayInsuranceIndex = -1;
    let insuranceActivity: Activity | undefined;
    if (activity.isInsuranceRequired) {
      insuranceActivity = memberActivities.find((insurance) => insurance.isInsurance);
      if (!insuranceActivity) return;

      paidInsuranceIndex = insuranceProductsPaid.findIndex((insuranceProduct) => insuranceProduct.activityId === insuranceActivity?.id && insuranceProduct.startDate >= periodicProductCheckbox.product.startDate && insuranceProduct.endDate <= periodicProductCheckbox.product.endDate);
      toPayInsuranceIndex = activitiesFeesToPayPayments.findIndex((insuranceProductPayment) => insuranceProductPayment.product.activityId === insuranceActivity?.id && insuranceProductPayment.product.startDate >= periodicProductCheckbox.product.startDate && insuranceProductPayment.product.endDate <= periodicProductCheckbox.product.endDate);
    }

    if (index >= 0) {
      setActivitiesFeesToPayPayments(activitiesFeesToPayPayments.filter((ppp) => ppp.product.id !== periodicProductCheckbox.product.id));
      if (activity.isInsuranceRequired && toPayInsuranceIndex >= 0 && insuranceActivity) {
        const insuranceProduct = getInsuranceProductToPay(insuranceActivity.id, periodicProductCheckbox.product.startDate, periodicProductCheckbox.product.endDate);
        if (insuranceProduct) {
          setActivitiesFeesToPayPayments(activitiesFeesToPayPayments.filter((ppp) => ppp.product.id !== periodicProductCheckbox.product.id && ppp.product.id !== insuranceProduct.id));
        }
      }
    }
    else {
      setActivitiesFeesToPayPayments([...activitiesFeesToPayPayments, periodicProductCheckbox]);
      if (activity.isInsuranceRequired) {
        if (paidInsuranceIndex < 0 && toPayInsuranceIndex < 0 && insuranceActivity) {
          const insuranceProduct = getInsuranceProductToPay(insuranceActivity.id, periodicProductCheckbox.product.startDate, periodicProductCheckbox.product.endDate);
          const insuranceProductPayment = getInsuranceProductToPayPayment(insuranceActivity.id, periodicProductCheckbox.product.startDate, periodicProductCheckbox.product.endDate);
          if (insuranceProduct && insuranceProductPayment) {
            setActivitiesFeesToPayPayments([...activitiesFeesToPayPayments, periodicProductCheckbox, insuranceProductPayment]);
          }
        }
      }
    }
  }

  const getPaymentSection = () => {
    if (activitiesFeesToPayPayments.length === 0) return <></>

    return (
      <SimpleGrid columns={[1, 1, 2]} spacing={8}>
        <GridItem>
          <Box
            bg={cardBg}
            p={6}
            borderRadius="lg"
            border="1px solid"
            borderColor={borderColor}
            boxShadow="lg"
          >
            <VStack spacing={4} align="stretch">
              <HStack>
                <Icon as={FaMoneyBillWave} color="green.500" />
                <Heading as="h3" size="md" color={textColor}>
                  Aranceles a abonar
                </Heading>
              </HStack>
              <Text color={secondaryTextColor}>
                Revisá atentamente. Vas a abonar los siguientes aranceles de actividades:
              </Text>
              <ProductsToPayList
                membershipFeesToPay={activitiesFeesToPayPayments}
                tableSize="sm"
                variant="default"
              />
            </VStack>
          </Box>
        </GridItem>
        <GridItem>
          <Box
            bg={cardBg}
            p={6}
            borderRadius="lg"
            border="1px solid"
            borderColor={borderColor}
            boxShadow="lg"
            position="sticky"
            top={6}
          >
            <PaymentMethods
              productsToPayList={activitiesFeesToPayPayments}
              description="Podés abonar tus aranceles de actividades pendientes de pago a través de los siguientes medios de pago. Es rápido, simple y seguro."
            />
          </Box>
        </GridItem>
      </SimpleGrid>
    )
  }

  return (
    <Container maxW="container.xl" py={6}>
      <VStack spacing={6} align="stretch">
        <Box
          bg={headerBg}
          p={6}
          borderRadius="lg"
          border="1px solid"
          borderColor={borderColor}
          boxShadow="sm"
        >
          <HStack>
            <Icon as={FaFutbol} color="green.500" boxSize={6} />
            <Heading color={textColor}>Mis actividades</Heading>
          </HStack>
          <Text mt={2} color={secondaryTextColor}>
            {subtitleText}
          </Text>
        </Box>

        {isLoading ? (
          <LoadingSpinner label="Cargando tus actividades..." />
        ) : (
          <VStack spacing={6} align="stretch">
            {memberActivitiesNoInsurance.length > 0 && (
              <Box
                bg={cardBg}
                borderRadius="lg"
                border="1px solid"
                borderColor={borderColor}
                boxShadow="sm"
                overflow="hidden"
                p={4}
              >
                <Tabs variant="soft-rounded" colorScheme="green" isFitted>
                  <TabList flexWrap="wrap" gap={2} mb={2}>
                    {memberActivitiesNoInsurance.map((activity) => (
                      <Tab key={activity.id}>
                        <Icon as={FaRunning} mr={2} />
                        {activity.name}
                      </Tab>
                    ))}
                  </TabList>
                  <TabPanels>
                    {memberActivitiesNoInsurance.map((activity) => (
                      <TabPanel key={activity.id} px={0} py={4}>
                        <VStack spacing={6} align="stretch">
                          <MemberActivityDetails activity={activity} />
                          <ActivityPaymentHistory
                            activityId={activity.id}
                            cardTitle={activity.name.toUpperCase()}
                            activitiesFeesYears={activitiesFeesYears}
                            activitiesFeesPayments={activitiesFeesPayments}
                            feesToPayPayments={activitiesFeesToPayPayments}
                            onCheckboxChange={handleCheckboxChange}
                          />
                        </VStack>
                      </TabPanel>
                    ))}
                  </TabPanels>
                </Tabs>
              </Box>
            )}

            {getPaymentSection()}
          </VStack>
        )}
      </VStack>
    </Container>
  )
}

export default MyActivities;
